<template>
  <v-layout
    column
    class="image-browser-body escalon-absolute-cover"
    :class="{'is-annotating': isAnnotating}"
    :style="ieStyle">
    <v-flex shrink>
      <v-row no-gutters>
        <v-col class="sidebar-controls secondary" :style="borderStyle">
          <top-annotation-controls :context="this.imageBrowserContext" />
        </v-col>
        <v-col class="main-side-controls secondary" :style="borderStyle">
          <image-playback-toolbar v-if="showPlaybackToolbar" />
        </v-col>
      </v-row>
    </v-flex>
    <v-flex class="absolute-cover-wrapper">
      <v-row no-gutters class="escalon-absolute-cover">
        <v-col v-if="isAnnotating" class="sidebar-controls all-controls tall-content-wrapper secondary" :style="borderStyle"> 
          <image-annotation-controls
            v-if="canAnnotate"
            :zoomControlSize="zoomControlSize"
            :zoomControlFit="zoomControlFit"
            :mainViewCanvasSize="largeViewSize"
            :transform="transform"
            @updateZoom="updateZoom"
            @zoomToFit="zoomToFit"
            @updateCenter="updateCenter"
            @stopFitZoom="stopFitZoom"/>
          <image-deep-dee-controls v-if="deepDeeAIEnabled"/>
          <image-measurement-controls
            v-if="showMeasurementSection && canAnnotate"
            :context="context"/>
        </v-col>
        <v-col 
          class="main-side-controls main-view-wrapper tall-content-wrapper">
          <main-image-view
            :largeViewSize="largeViewSize"
            :largeViewFit="largeViewFit"
            :rgbSize="rgbSize"
            :rgbFit="rgbFit"
            :transform="transform"
            :realLocation="realLocation"
            :isInBounds="isInBounds"
            :imgOffset="imgOffset"
            @panBy="panBy"/>    
          <image-drawer v-if="showImageDrawer" />
        </v-col>
      </v-row>
    </v-flex>
  </v-layout>
</template>

<script>
import topAnnotationControls from "./controls/TopAnnotationControls";
import imageAnnotationControls from "./controls/ImageAnnotationControls";
import imageMeasurementControls from "./controls/ImageMeasurementControls";
import imageDeepDeeControls from "./controls/ImageDeepDeeControls";
import imagePlaybackToolbar from "./controls/ImagePlaybackToolbar";
import mainImageView from "./MainImageView";
import imageDrawer from "./ImageDrawer";
import responsiveCanvases from "../mixins/ResponsiveCanvases";
import { mapGetters } from "vuex";
import { mapWatchers } from "../../helpers/utils";
import {
  defaultSize,
  getViewSize,
  setSize,
  setRGBSizes,
  setFittedSize,
  setFitZoom,
  setOffsets,
  defaultTransform,
  setZoom,
  setCenter,
  viewOffset,
  // withinViewBounds,
  calculateRealLocation,
  getFittedSize,
  setTransformSizes
} from "../../canvas/geometry";
export default {
  name: "imageBrowserBody",
  components: {
    topAnnotationControls,
    imageAnnotationControls,
    imageMeasurementControls,
    imagePlaybackToolbar,
    mainImageView,
    imageDeepDeeControls,
    imageDrawer
  },
  mixins: [responsiveCanvases],
  props: ["isOpen"],
  data() {
    return {
      zoomControlSize: defaultSize(),
      zoomControlFit: defaultSize(),
      largeViewSize: defaultSize(),
      largeViewFit: defaultSize(),
      fitImgSize: defaultSize(),
      rgbSize: defaultSize(),
      rgbFit: defaultSize(),
      mainWrapperId: "main-image-view",
      primaryCanvasWrapperId: "primary-canvas-wrapper",
      zoomWrapperId: "zoom-canvas-wrapper",
      transform: defaultTransform(),
      useFitZoom: true
    };
  },
  computed: {
    ...mapGetters([
      "imageBrowserContext",
      "isAnnotating",
      "rgbSettings",
      "exam",
      "displayImgSize",
      "flippedImgSize",
      "displayImgObject",
      "rgbView",
      "rgbExpanded",
      "showMeasurementSection",
      "showPlaybackToolbar",
      "canAnnotate",
      "isIE",
      "isFlipped90",
      "showImageDrawer",
      "maxZoom",
      "textAnnotationToEdit"
    ]),
    deepDeeAIEnabled(){
      return this.imageBrowserContext.device.drAIAnalysis;
    },
    imgOffset() {
      return viewOffset(this.largeViewSize, this.largeViewFit);
    },
    context() {
      return this.imageBrowserContext;
    },
    ieStyle() {
      if (!this.isIE) return null;
      return "margin-top: 35px";
    },
    canvasSizeFloor() {
      return getViewSize(this.mainWrapperId);
    }
  },
  methods: {
    keyboardEvent(e){
      if (!this.textAnnotationToEdit) 
      {
       if(e.keyCode == 37) {
         this.$store.dispatch("selectPreviousImage");
       }else if(e.keyCode == 39){
         this.$store.dispatch("selectNextImage");
       }
      }
    },
    updateCanvasSizes() {
      // logDetailed("ImageBrowserBody", "updateCanvasSizes");
      if (!this.displayImgSize) return;

      let mvs = getViewSize(this.mainWrapperId);
      // add some margins
      mvs.w -= 10;
      mvs.h -= 10;
      let zcs = getViewSize(this.zoomWrapperId);

      // set sizes of all containers
      setSize(this.zoomControlSize, zcs);
      setSize(this.largeViewSize, mvs);
      setSize(this.largeViewFit, mvs);
      setSize(this.rgbSize, defaultSize());

      // if we're in rgb view, we need to do some size adjustments to large and rgb
      if (this.rgbSettings)
        setRGBSizes(
          mvs,
          this.rgbSettings.expanded,
          this.largeViewSize,
          this.largeViewFit,
          this.rgbSize,
          this.exam.isOptos
        );
        
      // set sizes needed to fit images into all containers
      setFittedSize(this.rgbSize, this.displayImgSize, this.rgbFit, 15);
      setFittedSize(this.zoomControlSize, this.displayImgSize, this.zoomControlFit);

      let imgSize;
      if (this.isFlipped90)
        imgSize = this.flippedImgSize;
      else
        imgSize = this.displayImgSize;

      let lvFit = getFittedSize(this.largeViewSize, imgSize);
      
      // set transform image/view sizes
      setTransformSizes(this.transform, this.largeViewFit, imgSize);
      setFitZoom(this.transform, lvFit.zoom, this.useFitZoom);
      setCenter(this.transform, this.transform.centerX, this.transform.centerY);
      setOffsets(this.transform);

      this.$store.commit("setTransform", this.transform);
      // logDetailed("ImageBrowserBody", "updateCanvasSizes", 
      //   "mvs", mvs,
      //   "zoomControlSize", this.zoomControlSize,
      //   "zoomControlFit", this.zoomControlFit,
      //   "largeViewSize", this.largeViewSize, 
      //   "largeViewFit", this.largeViewFit, 
      //   "rgbSize", this.rgbSize);
    },
    updateZoom(zoom) {
      // logDetailed("ImageBrowserBody", "updateZoom", zoom);
      setZoom(this.transform, zoom, this.useFitZoom);
      setOffsets(this.transform);
      this.$store.commit("setTransform", this.transform);
    },
    updateCenter(center) {
      // logDetailed("ImageBrowserBody", "updateCenter", center);
      this.useFitZoom = false;
      setCenter(this.transform, center.x, center.y);
      setOffsets(this.transform);
    },
    zoomToFit() {
      this.useFitZoom = true;
      setZoom(this.transform, this.transform.fitZoom);
      setOffsets(this.transform);
      this.$store.commit("setTransform", this.transform);
    },
    stopFitZoom() {
      this.useFitZoom = false;
    },
    panBy(pan) {
      // logDetailed("ImageBrowserBody", "panBy", pan);
      let center = {
        x: this.transform.centerX + pan.dx / this.displayImgSize.w,
        y: this.transform.centerY + pan.dy / this.displayImgSize.h
      };
      this.updateCenter(center);
    },
    resetTransform() {
      this.transform = defaultTransform();
      if (this.imageBrowserContext.defaultZoom) 
        this.transform.defaultZoom = this.imageBrowserContext.defaultZoom;
      this.useFitZoom = true;
    },
    realLocation(x, y) {
      return calculateRealLocation(x, y, this.imgOffset, this.transform);
    },
    isInBounds(x, y) {
      return (
        x >= 0 &&
        y >= 0 &&
        x <= this.displayImgSize.w &&
        y <= this.displayImgSize.h
      );
    }
  },
  watch: {
    ...mapWatchers(
      [
        "rgbView",
        "rgbExpanded",
        "displayImgSize",
        "displayImgObject",
        "isFlipped90"
      ],
      function() {
        this.resetTransform();
        this.updateCanvasSizes();
      }
    ),
    isOpen() {
        this.resetTransform();
        this.resetView();
    }
  },
  mounted() {
    this.resetTransform();
    this.updateCanvasSizes();
    document.addEventListener("keydown", this.keyboardEvent);
  },
  destroyed() {
    document.removeEventListener("keydown", this.keyboardEvent);
  }
};
</script>
